import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';

import { safeTrim } from '../utils/stringUtils';
import MobileBottomNav from '../components/MobileBottomNav';
import { useAuthStore } from '../store/useAuthStore';
import { useToast } from '../context/ToastContext';
import { loginUser, registerUser, api } from '../utils/apiHelpers';
import { parseAuthError, validateRegistrationForm } from '../utils/errorHandler';

type StatusState =
  | { type: 'success'; message: string }
  | { type: 'error'; message: string }
  | null;

export default function AccountPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);
  const logout = useAuthStore((s) => s.logout);
  const { showToast } = useToast();

  const loginSectionRef = useRef<HTMLDivElement | null>(null);
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [status, setStatus] = useState<StatusState>(null);
  const [isLoginSubmitting, setIsLoginSubmitting] = useState(false);
  const [isRegisterSubmitting, setIsRegisterSubmitting] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [userOrders, setUserOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [ordersCount, setOrdersCount] = useState<number | null>(null);
  const [loginErrors, setLoginErrors] = useState<Record<string, string>>({});
  const [registerErrors, setRegisterErrors] = useState<Record<string, string>>({});

  const [loginData, setLoginData] = useState({
    identifier: '',
    password: '',
    remember: false,
  });

  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: '',
  });

  // Fetch user orders
  useEffect(() => {
    const fetchUserOrders = async () => {
      if (!user) {
        setUserOrders([]);
        setOrdersCount(null);
        return;
      }

      try {
        setOrdersLoading(true);
        setOrdersError(null);

        const userEmail = user.email;
        if (!userEmail) {
          setUserOrders([]);
          setOrdersCount(null);
          setOrdersLoading(false);
          return;
        }

        // Try multiple endpoints to fetch orders
        let ordersList: any[] = [];
        let foundOrders = false;

        const endpoints = [
          `/orders/by-email?email=${encodeURIComponent(userEmail)}`,
          `/orders/?email=${encodeURIComponent(userEmail)}`,
          `/orders/user/${user.id}`,
        ];

        for (const endpoint of endpoints) {
          try {
            const { data } = await api.get(endpoint);
            
            if (data) {
              if (Array.isArray(data?.orders)) {
                ordersList = data.orders;
                if (typeof data.count === 'number') {
                  setOrdersCount(data.count);
                } else {
                  setOrdersCount(ordersList.length);
                }
                foundOrders = true;
                break;
              } else if (Array.isArray(data?.results)) {
                ordersList = data.results;
                setOrdersCount(data.count || ordersList.length);
                foundOrders = true;
                break;
              } else if (Array.isArray(data)) {
                ordersList = data;
                setOrdersCount(ordersList.length);
                foundOrders = true;
                break;
              }
            }
          } catch (err) {
            // Try next endpoint
            continue;
          }
        }

        if (!foundOrders) {
          // No orders found, but don't show error - just empty state
          setUserOrders([]);
          setOrdersCount(0);
          return;
        }

        ordersList.sort((a: any, b: any) => {
          const dateA = new Date(a.order_date || a.created_at || 0).getTime();
          const dateB = new Date(b.order_date || b.created_at || 0).getTime();
          return dateB - dateA;
        });

        setUserOrders(ordersList);
      } catch (error) {
        console.error('Failed to fetch user orders:', error);
        // Don't show error - orders are optional
        setUserOrders([]);
        setOrdersCount(0);
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchUserOrders();
  }, [user]);

  const parseAmount = (value?: string | number | null) => {
    if (typeof value === 'number') return value;
    const numeric = parseFloat(value || '0');
    return Number.isNaN(numeric) ? 0 : numeric;
  };

  const formatCurrency = (value?: string | number | null) => {
    const amount = parseAmount(value);
    return `Rs ${amount.toLocaleString('en-PK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const registerAllowed =
    safeTrim(registerData.username) &&
    safeTrim(registerData.email) &&
    validateEmail(registerData.email) &&
    safeTrim(registerData.password).length >= 6 &&
    safeTrim(registerData.confirmPassword) === safeTrim(registerData.password);

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    // Reset errors
    setLoginErrors({});
    setStatus(null);

    // Validate inputs (defensive: guard against undefined)
    const errors: Record<string, string> = {};
    const rawIdentifier = loginData.identifier ?? '';
    const identifier = safeTrim(rawIdentifier);
    const rawPassword = loginData.password ?? '';
    const password = String(rawPassword);

    if (!identifier) {
      errors.identifier = 'Username or email is required';
    } else if (identifier.includes('@') && !validateEmail(identifier)) {
      errors.identifier = 'Please enter a valid email address';
    }

    if (!safeTrim(password)) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (Object.keys(errors).length > 0) {
      setLoginErrors(errors);
      setStatus({
        type: 'error',
        message: 'Please correct the errors below.',
      });
      return;
    }

    setIsLoginSubmitting(true);

    try {
      // Debug log raw inputs (safe for dev)
      console.log('🔐 Attempting login with identifier:', identifier);

      // Call real API login
      const response = await loginUser({
        identifier,
        password,
      });

      console.log('✅ Login successful! User data received:', {
        id: response.user.id,
        username: response.user.username,
        email: response.user.email,
        role: response.user.role,
        phone: (response.user as any)?.phone,
        address: (response.user as any)?.address,
        profile_pic: response.user.profile_pic,
        token: response.token.substring(0, 20) + '...',
        refresh: response.refresh?.substring(0, 20) + '...' || 'N/A',
      });

      // Debug: log full response before storing
      // eslint-disable-next-line no-console
      console.log('AccountPage login response raw:', response);
      // Save auth data to store (with persistence via zustand)
      setAuth({
        user: {
          id: response.user.id,
          username: response.user.username,
          email: response.user.email,
          role: response.user.role || 'customer',
          profile_pic: response.user.profile_pic || null,
          phone: (response.user as any)?.phone || '',
          address: (response.user as any)?.address || '',
        },
        token: response.token,
        refreshToken: response.refresh,
      });

      console.log('💾 User data saved to Zustand store');

      setStatus({
        type: 'success',
        message: 'Login successful. Redirecting to your account...',
      });
      
      setLoginErrors({});

      setTimeout(() => {
        showToast('Logged in successfully!', 'success');
        navigate('/');
      }, 600);
    } catch (error) {
      // Log raw error for debugging (preserve original stack)
      console.error('Raw login error object:', error);
      if (error && (error as any).stack) console.error((error as any).stack);
      const parsedError = parseAuthError(error);
      console.error('❌ Login error:', parsedError);
      
      setLoginErrors(parsedError.fieldErrors || {});
      setStatus({
        type: 'error',
        message: parsedError.message,
      });
      showToast(parsedError.message, 'error');
    } finally {
      setIsLoginSubmitting(false);
    }
  };

  const handleRegisterSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    // Reset errors
    setRegisterErrors({});
    setStatus(null);

    // Validate form
    const validation = validateRegistrationForm(registerData);
    
    if (!validation.isValid) {
      setRegisterErrors(validation.errors);
      setStatus({
        type: 'error',
        message: 'Please correct the errors below.',
      });
      return;
    }

    setIsRegisterSubmitting(true);

    try {
      // Call real API register
      const response = await registerUser({
        firstName: registerData.firstName,
        lastName: registerData.lastName,
        username: registerData.username,
        email: registerData.email,
        password: registerData.password,
        phone: registerData.phone || undefined,
        address: registerData.address || undefined,
      });

      console.log('✅ Registration successful! New user data:', {
        id: response.user.id,
        username: response.user.username,
        email: response.user.email,
        firstName: registerData.firstName,
        lastName: registerData.lastName,
        role: response.user.role,
        phone: (response.user as any)?.phone,
        address: (response.user as any)?.address,
        profile_pic: response.user.profile_pic,
        token: response.token.substring(0, 20) + '...',
        refresh: response.refresh?.substring(0, 20) + '...' || 'N/A',
      });

      setStatus({
        type: 'success',
        message: `Account created successfully. Logging you in...`,
      });

      // Auto-login after successful registration
      setAuth({
        user: {
          id: response.user.id,
          username: response.user.username,
          email: response.user.email,
          role: response.user.role || 'customer',
          profile_pic: response.user.profile_pic || null,
          phone: (response.user as any)?.phone || '',
          address: (response.user as any)?.address || '',
        },
        token: response.token,
        refreshToken: response.refresh,
      });

      console.log('💾 New user data saved to Zustand store');

      setRegisterErrors({});

      setTimeout(() => {
        showToast('Account created and logged in successfully!', 'success');
        navigate('/');
      }, 600);

      setRegisterData({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
        address: '',
      });
      setIsRegisterMode(false);
    } catch (error) {
      const parsedError = parseAuthError(error);
      console.error('❌ Registration error:', parsedError);
      
      setRegisterErrors(parsedError.fieldErrors || {});
      setStatus({
        type: 'error',
        message: parsedError.message,
      });
      showToast(parsedError.message, 'error');
    } finally {
      setIsRegisterSubmitting(false);
    }
  };

  // Log user data when profile loads
  useEffect(() => {
    if (user) {
      console.log('👤 User Profile Loaded:', {
        id: user.id,
        username: user.username,
        email: user.email,
        role: user.role,
        phone: (user as any)?.phone,
        address: (user as any)?.address,
        profile_pic: user.profile_pic,
        fullData: user,
      });
    }
  }, [user]);

  // Logged in user profile
  if (user) {
    const displayName = user.username || 'Customer';
    const email = user.email || 'Not provided';
    const rawPhone = (user as any)?.phone;
    const phone = safeTrim(rawPhone) !== '' ? safeTrim(rawPhone) : 'Not provided';
    const rawAddress = (user as any)?.address;
    const address = safeTrim(rawAddress) !== '' ? safeTrim(rawAddress) : 'Not provided';
    const role = user.role || 'customer';
    
    // Format phone number for display
    const formattedPhone = phone !== 'Not provided' ? phone : '—';
    const formattedAddress = address !== 'Not provided' ? address : 'No address saved yet.';

    // Log to console for debugging
    console.log('📊 Profile Data:', {
      displayName,
      email,
      phone: formattedPhone,
      address: formattedAddress,
      role,
    });

    // Store user email for order key generation
    const userEmail = user.email || '';

    return (
      <div className="min-h-screen bg-[#f7f7fb] text-gray-900">
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <Link to="/" className="hover:text-gray-900">
              Home
            </Link>
            <span>/</span>
            <span className="text-gray-900 font-semibold">My profile</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-semibold text-gray-900">
                Hello, {displayName}
              </h1>
              <p className="text-gray-600 mt-2">
                Manage your personal details, track orders, and access quick account actions.
              </p>
            </div>
            <button
              onClick={() => {
                console.log('🔓 User logging out:', { username: user.username, email: user.email });
                logout();
                console.log('✅ Logout successful - session cleared');
                showToast('Logged out successfully!', 'success');
                navigate('/');
              }}
              className="self-start md:self-auto inline-flex items-center gap-2 rounded-full border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Logout
            </button>
          </div>

          <div className="grid gap-6 lg:grid-cols-3 mt-8">
            <section className="lg:col-span-2 bg-white rounded-[32px] border border-gray-100 shadow-sm p-6 sm:p-8 space-y-6">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-gray-400">Profile summary</p>
                <h2 className="text-2xl font-semibold text-gray-900 mt-2">Personal details</h2>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <p className="text-sm text-gray-500">Full name</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">{displayName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Role</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1 capitalize">{role}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1 break-words">{email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1 font-mono">{formattedPhone}</p>
                </div>
              </div>

              <div className="border-2 border-orange-100 rounded-3xl p-5 bg-orange-50">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-2">📍 Default Address</p>
                    <p className="text-gray-900 font-medium leading-relaxed">{formattedAddress}</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-[32px] border border-gray-100 shadow-sm p-6 sm:p-8 space-y-5">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-gray-400">Quick actions</p>
                <h2 className="text-xl font-semibold text-gray-900 mt-2">Manage your account</h2>
              </div>

              <div className="space-y-3">
                <Link
                  to="/shop"
                  className="block rounded-2xl border border-gray-200 px-4 py-3 hover:border-orange-400 hover:bg-orange-50 transition-colors"
                >
                  <p className="font-semibold text-gray-900">Continue shopping</p>
                  <p className="text-sm text-gray-500">Browse new arrivals and recommendations.</p>
                </Link>
                <Link
                  to="/wishlist"
                  className="block rounded-2xl border border-gray-200 px-4 py-3 hover:border-orange-400 hover:bg-orange-50 transition-colors"
                >
                  <p className="font-semibold text-gray-900">Wishlist</p>
                  <p className="text-sm text-gray-500">Quickly view items you've saved for later.</p>
                </Link>
              </div>
            </section>
          </div>
        </main>

        {/* User Orders */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="mt-4 bg-white rounded-[32px] border border-gray-100 shadow-sm p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-gray-400">Order history</p>
                <h2 className="text-xl font-semibold text-gray-900 mt-1">
                  Your orders
                  {ordersCount !== null && (
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      ({ordersCount} {ordersCount === 1 ? 'order' : 'orders'})
                    </span>
                  )}
                </h2>
              </div>
            </div>

            {ordersLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mb-3" />
                  <p className="text-sm text-gray-600">Loading your orders...</p>
                </div>
              </div>
            ) : ordersError ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {ordersError}
              </div>
            ) : userOrders.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-600">
                You have not placed any orders yet.
              </div>
            ) : (
              <div className="space-y-4">
                {userOrders.map((order: any, index: number) => {
                  const orderItems = order.items || order.order_items || [];
                  const orderTotal = parseAmount(order.total_amount || order.subtotal);
                  const orderDate = order.order_date
                    ? new Date(order.order_date)
                    : order.created_at
                      ? new Date(order.created_at)
                      : null;
                  const formattedOrderDate = orderDate
                    ? orderDate.toLocaleDateString('en-US', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })
                    : '--';

                  return (
                    <div
                      key={order.id || `${userEmail}-${index}`}
                      className="border border-gray-200 rounded-xl p-4 hover:border-orange-400 hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-semibold text-gray-900">Order #{order.id}</h3>
                            <span className="text-xs rounded-full bg-gray-100 px-2 py-0.5 text-gray-700 capitalize">
                              {order.status?.replace(/_/g, ' ') || 'pending'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">Date: {formattedOrderDate}</p>
                          <p className="text-sm text-gray-600">
                            {orderItems.length} {orderItems.length === 1 ? 'item' : 'items'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-orange-500">{formatCurrency(orderTotal)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

       
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f7fb] text-gray-900">
      

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <Link to="/" className="hover:text-gray-900">
            Home
          </Link>
          <span>/</span>
          <span className="text-gray-900 font-semibold">My account</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 mt-4 mb-8">
          My Account
        </h1>

        {status && (
          <div
            className={`mb-6 flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${
              status.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            {status.type === 'success' ? (
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
            )}
            <p>{status.message}</p>
          </div>
        )}

        <section className="relative bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex flex-col lg:flex-row lg:items-stretch min-h-[420px]">
            {/* Side Panel */}
            <div className="flex-1 min-h-[360px] lg:min-h-[520px] p-8 lg:p-10 bg-gradient-to-br from-[#fff4e5] to-[#ffe1b0] flex flex-col items-center lg:items-start justify-center">
              <p className="uppercase tracking-[0.3em] text-[10px] text-gray-600 opacity-75 mb-2">
                {isRegisterMode ? 'Already registered?' : 'New to Dehleze?'}
              </p>
              <h2 className="text-2xl lg:text-[28px] font-semibold text-gray-900 mb-3">
                {isRegisterMode ? 'Welcome back!' : 'Create your account'}
              </h2>
              <p className="text-[13px] leading-relaxed mb-6 max-w-sm mx-auto lg:mx-0 text-gray-700">
                {isRegisterMode
                  ? 'Log in to access your dashboard, track orders, and enjoy faster checkout.'
                  : 'Join us to track your orders, manage your wishlist, and receive personalized offers.'}
              </p>
              <button
                type="button"
                onClick={() => {
                  setStatus(null);
                  setIsRegisterMode((prev) => !prev);
                  if (!isRegisterMode && loginSectionRef.current) {
                    setTimeout(() => {
                      loginSectionRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                    }, 50);
                  }
                }}
                className="px-7 py-2.5 rounded-full text-[13px] font-semibold bg-[#0d0625] text-white hover:bg-[#1e0b3c] transition"
              >
                {isRegisterMode ? 'Go to Login' : 'Switch to Register'}
              </button>
            </div>

            {/* Forms */}
            <div className="flex-1 min-h-[360px] lg:min-h-[520px] p-8 lg:p-10 flex items-center">
              {isRegisterMode ? (
                <div className="w-full">
                  <p className="uppercase tracking-[0.4em] text-[10px] text-gray-500 mb-2">Register</p>
                  <h2 className="text-xl font-semibold text-gray-900 mb-5">Create an account</h2>
                  <form className="space-y-5" onSubmit={handleRegisterSubmit}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label className="block text-xs font-semibold text-gray-600">
                        First name *
                        <input
                          type="text"
                          value={registerData.firstName}
                          onChange={(e) =>
                            setRegisterData((prev) => ({ ...prev, firstName: e.target.value }))
                          }
                          className={`mt-2 w-full rounded-full border ${
                            registerErrors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                          } px-3 py-2.5 text-[13px] text-gray-900 focus:outline-none focus:ring-2 ${
                            registerErrors.firstName ? 'focus:border-red-400 focus:ring-red-100' : 'focus:border-[#fd9e10] focus:ring-[#fd9e10]/15'
                          }`}
                          placeholder="Jane"
                        />
                        {registerErrors.firstName && (
                          <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> {registerErrors.firstName}
                          </p>
                        )}
                      </label>
                      <label className="block text-xs font-semibold text-gray-600">
                        Last name *
                        <input
                          type="text"
                          value={registerData.lastName}
                          onChange={(e) =>
                            setRegisterData((prev) => ({ ...prev, lastName: e.target.value }))
                          }
                          className={`mt-2 w-full rounded-full border ${
                            registerErrors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                          } px-3 py-2.5 text-[13px] text-gray-900 focus:outline-none focus:ring-2 ${
                            registerErrors.lastName ? 'focus:border-red-400 focus:ring-red-100' : 'focus:border-[#fd9e10] focus:ring-[#fd9e10]/15'
                          }`}
                          placeholder="Doe"
                        />
                        {registerErrors.lastName && (
                          <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> {registerErrors.lastName}
                          </p>
                        )}
                      </label>
                    </div>

                    <label className="block text-xs font-semibold text-gray-600">
                      Email address *
                      <input
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData((prev) => ({ ...prev, email: e.target.value }))}
                        className={`mt-2 w-full rounded-full border ${
                          registerErrors.email ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                        } px-3 py-2.5 text-[13px] text-gray-900 focus:outline-none focus:ring-2 ${
                          registerErrors.email ? 'focus:border-red-400 focus:ring-red-100' : 'focus:border-[#fd9e10] focus:ring-[#fd9e10]/15'
                        }`}
                        placeholder="you@example.com"
                      />
                      {registerErrors.email && (
                        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> {registerErrors.email}
                        </p>
                      )}
                    </label>

                    <label className="block text-xs font-semibold text-gray-600">
                      Username *
                      <input
                        type="text"
                        value={registerData.username}
                        onChange={(e) => setRegisterData((prev) => ({ ...prev, username: e.target.value }))}
                        className={`mt-2 w-full rounded-full border ${
                          registerErrors.username ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                        } px-3 py-2.5 text-[13px] text-gray-900 focus:outline-none focus:ring-2 ${
                          registerErrors.username ? 'focus:border-red-400 focus:ring-red-100' : 'focus:border-[#fd9e10] focus:ring-[#fd9e10]/15'
                        }`}
                        placeholder="Choose a unique username"
                      />
                      {registerErrors.username && (
                        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> {registerErrors.username}
                        </p>
                      )}
                    </label>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label className="block text-xs font-semibold text-gray-600">
                        Phone number
                        <input
                          type="tel"
                          value={registerData.phone}
                          onChange={(e) => setRegisterData((prev) => ({ ...prev, phone: e.target.value }))}
                          className="mt-2 w-full rounded-full border border-gray-200 bg-gray-50 px-3 py-2.5 text-[13px] text-gray-900 focus:border-[#fd9e10] focus:outline-none focus:ring-2 focus:ring-[#fd9e10]/15"
                          placeholder="+1 (555) 000-0000"
                        />
                      </label>
                      <label className="block text-xs font-semibold text-gray-600">
                        Address
                        <input
                          type="text"
                          value={registerData.address}
                          onChange={(e) => setRegisterData((prev) => ({ ...prev, address: e.target.value }))}
                          className="mt-2 w-full rounded-full border border-gray-200 bg-gray-50 px-3 py-2.5 text-[13px] text-gray-900 focus:border-[#fd9e10] focus:outline-none focus:ring-2 focus:ring-[#fd9e10]/15"
                          placeholder="123 Main St, City, State 12345"
                        />
                      </label>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label className="block text-xs font-semibold text-gray-600">
                        Password *
                        <div className="mt-2 relative">
                          <input
                            type={showRegisterPassword ? 'text' : 'password'}
                            value={registerData.password}
                            onChange={(e) => setRegisterData((prev) => ({ ...prev, password: e.target.value }))}
                            className={`w-full rounded-full border ${
                              registerErrors.password ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                            } px-3 py-2.5 text-[13px] pr-11 text-gray-900 focus:outline-none focus:ring-2 ${
                              registerErrors.password ? 'focus:border-red-400 focus:ring-red-100' : 'focus:border-[#fd9e10] focus:ring-[#fd9e10]/15'
                            }`}
                            placeholder="At least 6 characters"
                          />
                          <button
                            type="button"
                            onClick={() => setShowRegisterPassword((prev) => !prev)}
                            className="absolute inset-y-0 right-4 flex items-center text-gray-500 hover:text-gray-900"
                          >
                            {showRegisterPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        </div>
                        {registerErrors.password && (
                          <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> {registerErrors.password}
                          </p>
                        )}
                      </label>
                      <label className="block text-xs font-semibold text-gray-600">
                        Confirm password *
                        <input
                          type={showRegisterPassword ? 'text' : 'password'}
                          value={registerData.confirmPassword}
                          onChange={(e) =>
                            setRegisterData((prev) => ({ ...prev, confirmPassword: e.target.value }))
                          }
                          className={`mt-2 w-full rounded-full border ${
                            registerErrors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                          } px-3 py-2.5 text-[13px] text-gray-900 focus:outline-none focus:ring-2 ${
                            registerErrors.confirmPassword ? 'focus:border-red-400 focus:ring-red-100' : 'focus:border-[#fd9e10] focus:ring-[#fd9e10]/15'
                          }`}
                          placeholder="Re-enter password"
                        />
                        {registerErrors.confirmPassword && (
                          <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> {registerErrors.confirmPassword}
                          </p>
                        )}
                      </label>
                    </div>

                    <button
                      type="submit"
                      className="w-full rounded-full bg-gray-900 py-2.5 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:bg-gray-300"
                      disabled={isRegisterSubmitting || !registerAllowed}
                    >
                      {isRegisterSubmitting ? 'Processing...' : 'Register'}
                    </button>

                    <p className="text-[11px] text-gray-500">
                      By creating an account, you agree to our{' '}
                      <Link to="/privacy" className="text-[#fd9e10] font-medium hover:text-[#f98700]">
                        privacy policy
                      </Link>{' '}
                      and{' '}
                      <Link to="/terms" className="text-[#fd9e10] font-medium hover:text-[#f98700]">
                        terms of service
                      </Link>
                      .
                    </p>
                  </form>
                </div>
              ) : (
                <div className="w-full" ref={loginSectionRef}>
                  <p className="uppercase tracking-[0.4em] text-[10px] text-gray-500 mb-2">Login</p>
                  <h2 className="text-xl font-semibold text-gray-900 mb-5">Welcome back</h2>
                  <form className="space-y-5" onSubmit={handleLoginSubmit}>
                    <label className="block text-xs font-semibold text-gray-600">
                      Username or email address *
                      <input
                        type="text"
                        value={loginData.identifier}
                        onChange={(e) => setLoginData((prev) => ({ ...prev, identifier: e.target.value }))}
                        className={`mt-2 w-full rounded-full border ${
                          loginErrors.identifier ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                        } px-3 py-2.5 text-[13px] text-gray-900 focus:outline-none focus:ring-2 ${
                          loginErrors.identifier ? 'focus:border-red-400 focus:ring-red-100' : 'focus:border-[#fd9e10] focus:ring-[#fd9e10]/15'
                        }`}
                        placeholder="E.g. abc@example.com"
                      />
                      {loginErrors.identifier && (
                        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> {loginErrors.identifier}
                        </p>
                      )}
                    </label>

                    <label className="block text-xs font-semibold text-gray-600">
                      Password *
                      <div className="mt-2 relative">
                        <input
                          type={showLoginPassword ? 'text' : 'password'}
                          value={loginData.password}
                          onChange={(e) => setLoginData((prev) => ({ ...prev, password: e.target.value }))}
                          className={`w-full rounded-full border ${
                            loginErrors.password ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
                          } px-3 py-2.5 text-[13px] pr-11 text-gray-900 focus:outline-none focus:ring-2 ${
                            loginErrors.password ? 'focus:border-red-400 focus:ring-red-100' : 'focus:border-[#fd9e10] focus:ring-[#fd9e10]/15'
                          }`}
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword((prev) => !prev)}
                          className="absolute inset-y-0 right-4 flex items-center text-gray-500 hover:text-gray-900"
                        >
                          {showLoginPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {loginErrors.password && (
                        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> {loginErrors.password}
                        </p>
                      )}
                    </label>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[13px]">
                      <label className="inline-flex items-center space-x-2 cursor-pointer select-none text-gray-600">
                        <input
                          type="checkbox"
                          checked={loginData.remember}
                          onChange={(e) => setLoginData((prev) => ({ ...prev, remember: e.target.checked }))}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <span>Remember me</span>
                      </label>
                      <Link to="/account/reset-password" className="text-[#fd9e10] font-medium hover:text-[#f98700]">
                        Lost your password?
                      </Link>
                    </div>

                    <button
                      type="submit"
                      className="w-full rounded-full bg-[#fd9e10] py-2.5 text-sm font-semibold text-white transition hover:bg-[#f98700] disabled:bg-orange-300"
                      disabled={isLoginSubmitting}
                    >
                      {isLoginSubmitting ? 'Processing...' : 'Log in'}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      
      <MobileBottomNav />
    </div>
  );
}
